/* eslint-disable no-shadow */
import * as React from 'react';

const DESKTOP_BREAKPOINT = 1024;
const MOBILE_BREAKPOINT = 640;

export enum DeviceType {
  MOBILE = 'mobile', // < 640px
  TABLET = 'tablet', // 640px - 1023px
  DESKTOP = 'desktop', // ≥ 1024px
}

export function useDeviceType() {
  const [deviceType, setDeviceType] = React.useState<DeviceType | undefined>(
    undefined
  );
  const [orientation, setOrientation] = React.useState<
    'portrait' | 'landscape'
  >('portrait');

  React.useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      let deviceTypeValue: DeviceType;
      if (width < MOBILE_BREAKPOINT) {
        deviceTypeValue = DeviceType.MOBILE;
      } else if (width < DESKTOP_BREAKPOINT) {
        deviceTypeValue = DeviceType.TABLET;
      } else {
        deviceTypeValue = DeviceType.DESKTOP;
      }

      // Determine orientation
      const newOrientation = width > height ? 'landscape' : 'portrait';

      setDeviceType(deviceTypeValue);
      setOrientation(newOrientation);
    };

    // Debounce resize events for better performance
    let timeoutId: NodeJS.Timeout;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDeviceInfo, 100);
    };

    // Initial call
    updateDeviceInfo();

    // Listen for resize events
    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', updateDeviceInfo);

    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', updateDeviceInfo);
      clearTimeout(timeoutId);
    };
  }, []);

  return {
    deviceType: deviceType || DeviceType.DESKTOP,
    orientation,
    isMobile: deviceType === DeviceType.MOBILE,
    isTablet: deviceType === DeviceType.TABLET,
    isDesktop: deviceType === DeviceType.DESKTOP,
    isMobileOrTablet:
      deviceType === DeviceType.MOBILE || deviceType === DeviceType.TABLET,
  };
}

export function useIsMobile() {
  const { isMobileOrTablet } = useDeviceType();
  return isMobileOrTablet;
}
